// Ad-click attribution capture.
//
// Google Ads appends a click identifier to the landing URL (gclid, or wbraid /
// gbraid on privacy-restricted paths). The landing page is almost never the
// form page, so the value has to be stashed on arrival and read back at submit
// time. Without this we cannot tell a paid click from organic traffic, which
// means we cannot tell whether a junk lead cost money.

const STORAGE_KEY = 'lemay_attribution';

const CLICK_ID_PARAMS = ['gclid', 'wbraid', 'gbraid'] as const;
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

export interface Attribution {
  gclid?: string;
  wbraid?: string;
  gbraid?: string;
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  landing_page?: string;
  referrer?: string;
  captured_at?: string;
}

const readStored = (): Attribution => {
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Attribution) : {};
  } catch {
    return {};
  }
};

/**
 * Read click identifiers off the current URL and remember them for the session.
 *
 * First touch wins for UTM values, but a fresh click identifier always
 * overwrites: a visitor who clicks a second ad mid-session should be attributed
 * to that second click.
 */
export const captureAttribution = (): void => {
  if (typeof window === 'undefined') return;

  try {
    const params = new URLSearchParams(window.location.search);
    const stored = readStored();
    const incoming: Attribution = {};

    CLICK_ID_PARAMS.forEach(key => {
      const value = params.get(key);
      if (value) incoming[key] = value;
    });

    UTM_PARAMS.forEach(key => {
      const value = params.get(key);
      if (value && !stored[key]) incoming[key] = value;
    });

    // Nothing new on this URL and we already have a record: leave it alone.
    if (Object.keys(incoming).length === 0 && Object.keys(stored).length > 0) return;

    const merged: Attribution = {
      ...stored,
      ...incoming,
      landing_page: stored.landing_page || window.location.pathname,
      referrer: stored.referrer || document.referrer || undefined,
      captured_at: stored.captured_at || new Date().toISOString(),
    };

    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    // sessionStorage can be unavailable (private mode, blocked cookies).
    // Attribution is diagnostic, it must never break the page.
  }
};

/**
 * Attribution for the current session, or null if none was captured.
 */
export const getAttribution = (): Attribution | null => {
  if (typeof window === 'undefined') return null;
  const stored = readStored();
  return Object.keys(stored).length > 0 ? stored : null;
};

/**
 * True when this session arrived on a Google Ads click.
 */
export const isPaidClick = (): boolean => {
  const attribution = getAttribution();
  if (!attribution) return false;
  return CLICK_ID_PARAMS.some(key => Boolean(attribution[key]));
};
